import {
  IAccommodation,
  IFilter,
  IMap,
  IProviderProductObject,
  ISaveVehicleAccommodations,
  IUpdateVehicleAccessAndCapacity,
  IUpdateVehicleProvidersAndProviderProducts,
  IUpdateVehicleProviderProducts,
  IVehicleCapacities,
  IVehicleCreateOptions,
  IVehicleInfo,
  IVehicleObj,
  IVehicleProviderObj,
  IVehicleProvidersAndProductsIds,
  IVehicleResponseDTO,
  IVehicleListWithOperatorResponse,
  IOperatorIdsDTO
} from '../types'
import Paths from '../utils/paths'
import { serverApi } from '../utils/request'
import {
  IFetchSingleVehiclesSuccess,
  ISaveVehicleDepotAddressPayload,
  IUpdateVehicleDepotAddressPayload,
  IVehicleFilters
} from '../types/vehicle'

class ServiceVehicles {
  public async fetchAllVehicles(params: IVehicleFilters): Promise<any> {
    return await serverApi.get<any>(Paths.vehicles.list, { params })
  }

  public async fetchSingleVehicle(id: string): Promise<IFetchSingleVehiclesSuccess> {
    try {
      const res = await serverApi.get<IVehicleResponseDTO>(`${Paths.vehicles.base}/${id}`)
      const { products, accommodations, ...restVehicle } = res.data
      const normalizedVehicle = {
        ...restVehicle,
        products,
        accommodations
      }

      const providerProducts = products.reduce(
        (acc, cur) => {
          return { ...acc, [cur.id]: cur }
        },
        {} as IMap<IProviderProductObject>
      )

      const vehicleAccommodations = accommodations.reduce(
        (acc, cur) => ({ [cur.accommodationId]: { id: cur.accommodationId, ...cur } }),
        {} as IMap<IAccommodation>
      )

      return {
        vehicle: { [normalizedVehicle.id]: normalizedVehicle },
        providerProducts,
        accommodations: vehicleAccommodations
      }
    } catch (e) {
      console.log('got error: ', e)
      throw new Error(e)
    }
  }

  public activateVehicle(id: string): Promise<any> {
    return serverApi.put(Paths.vehicles.changeActiveStatus, {
      id,
      isActive: true
    })
  }

  public deactivateVehicle(id: string): Promise<any> {
    return serverApi.put(Paths.vehicles.changeActiveStatus, {
      id,
      isActive: false
    })
  }

  public async removeVehicle(vehicleId: string): Promise<any> {
    const path = Paths.vehicles.remove.replace(':id', vehicleId)
    return await serverApi.delete(path)
  }

  public async createOne(data: IVehicleInfo): Promise<IVehicleObj> {
    const savedVehicle = await serverApi.post<IVehicleObj>(Paths.vehicles.base, data)
    return savedVehicle.data
  }

  public async createVehicleType(name: string): Promise<any> {
    return await serverApi.post<any>(Paths.vehicleType.create, {
      name
    })
  }

  public async removeVehicleType(id: string): Promise<any> {
    const path = Paths.vehicleType.remove.replace(':id', id)
    return await serverApi.delete(path)
  }

  public async createVehicleColor(name: string): Promise<any> {
    return await serverApi.post<any>(Paths.vehicleColor.create, {
      name
    })
  }

  public async removeVehicleColor(id: string): Promise<any> {
    const path = Paths.vehicleColor.remove.replace(':id', id)
    return await serverApi.delete(path)
  }

  public async createVehicleMakes(name: string): Promise<any> {
    return await serverApi.post<any>(Paths.vehicleMakes.create, {
      name
    })
  }

  public async removeVehicleMakes(id: string): Promise<any> {
    const path = Paths.vehicleMakes.remove.replace(':id', id)
    return await serverApi.delete(path)
  }

  public async createVehicleModel(name: string, makeId: string): Promise<any> {
    return await serverApi.post<any>(Paths.vehicleModel.create, {
      name,
      makeId
    })
  }

  public async removeVehicleModel(id: string): Promise<any> {
    const path = Paths.vehicleModel.remove.replace(':id', id)
    return await serverApi.delete(path)
  }

  public async getAccommodations(): Promise<IMap<IAccommodation>> {
    const vehicleAccommodation = await serverApi.get<IAccommodation[]>(Paths.vehicleAccommodation.getAll).catch(e => {
      throw new Error(e.data)
    })
    return vehicleAccommodation.data.reduce((acc, cur) => ({ ...acc, ...{ [cur.id]: cur } }), {} as IMap<
      IAccommodation
    >)
  }

  public async updateVehicleProvidersAndProviderProducts(
    data: IUpdateVehicleProvidersAndProviderProducts
  ): Promise<void> {
    await serverApi.put<void>(Paths.vehicles.updateProvidersAndProviderProducts, data).catch(e => {
      throw new Error(e.data)
    })
    return
  }

  public async updateVehicleProviderProducts(data: IUpdateVehicleProviderProducts): Promise<void> {
    await serverApi.put<void>(Paths.vehicles.updateProviderProducts, data).catch(e => {
      throw new Error(e.data)
    })
    return
  }

  public async updateVehicleAccessAndCapacity(data: IUpdateVehicleAccessAndCapacity): Promise<void> {
    await serverApi.put<void>(Paths.vehicles.updateCapacity, data)
    return
  }

  public async updateVehicleAccommodations(data: ISaveVehicleAccommodations): Promise<void> {
    try {
      await serverApi.put<void>(Paths.vehicles.updateAccommodations, data)
    } catch (e) {
      throw e.data
    }
  }

  public async fetchVehicleCreateOptions(): Promise<any> {
    return await serverApi.get(Paths.vehicles.getCreationOptions)
  }

  public async fetchVehicleCreateAccomodations(): Promise<any> {
    return await serverApi.get(Paths.vehicleAccommodation.getAll)
  }

  public async createVehicleCreateAccomodations(name: string): Promise<any> {
    return await serverApi.post<any>(Paths.vehicleAccommodation.create, {
      name
    })
  }

  public async removeVehicleCreateAccomodations(id: string): Promise<any> {
    const path = Paths.vehicleAccommodation.remove.replace(':id', id)
    return await serverApi.delete(path)
  }

  public async updateOne(data: IVehicleInfo): Promise<IVehicleObj> {
    if (data.fleetId === undefined) {
      data.fleetId = '' //This has not null constraint in DB, but is unused
    }
    return serverApi.put<IVehicleObj>(`${Paths.vehicles.base}/${data.id}`, data).then(({ data }) => data)
  }

  public async unassignOperator(id: string, operatorId: string): Promise<any> {
    const path = Paths.vehicles.unassignOperator.replace(':id', id).replace(':operatorId', operatorId)
    return await serverApi.delete<void>(path)
  }

  public async assignOperator(vehicleId: string, operatorId: string): Promise<any> {
    const path = `${Paths.apiV1BaseUrl}/mobile/operators/doSetVehicle`
    return await serverApi.patch<any>(path, { vehicleId, operatorId })
  }

  public async getVehicleByOperatorIds(data: IOperatorIdsDTO): Promise<any> {
    return await serverApi.post<any>(`${Paths.vehicles.getVehicleByOperatorIds}`, data)
  }

  public async saveVehicleDepotAddress(data: ISaveVehicleDepotAddressPayload, vehicleId: string): Promise<any> {
    return await serverApi.post<any>(`${Paths.vehicles.saveVehicleDepotAddress.replace(`:vehicleId`, vehicleId)}`, data)
  }

  public async updateVehicleDepotAddress(data: IUpdateVehicleDepotAddressPayload): Promise<any> {
    return await serverApi.put<any>(`${Paths.vehicles.updateVehicleDepotAddress}`, data)
  }

  public async deleteVehicleDepotAddress(id: string): Promise<any> {
    return await serverApi.delete<any>(`${Paths.vehicles.deleteVehicleDepotAddress.replace(':id', id)}`)
  }

  public async getVehicleDepotAddress(vehicleId: string): Promise<any> {
    const response = await serverApi.get<any>(
      `${Paths.vehicles.getVehicleDepotAddress.replace(':vehicleId', vehicleId)}`
    )
    return response.data
  }
}

const serviceVehicles = new ServiceVehicles()

export default serviceVehicles



// WEBPACK FOOTER //
// ./src/services/Vehicles.ts